import { useRef } from "react";
import { ScoreDetailContext, type FetchPostForAnswers } from "./context";
import { Score } from "src/types";
import { ScoreDetailBuilder } from "./ScoreDetailBuilder";
import { useDrilldown } from "./useDrilldown";
import { TabConfig } from "src/components/ScoreDetail/InnerTabs/ScoreDetailTabs";
import { ScoreWithVariation } from "src/services/ScoreService";
import { VariationsParams } from "../OlapScores/useVariations";
import { DimensionValue, MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresParams } from "../OlapScores/useOlapScoresParams";
import { OlapLocalizedLabelContext, OlapLocalizedLabelService } from "../OlapScores/OlapLocalizedLabelService";
import { OlapHeadingColorService } from "../OlapScores/OlapHeadingColorService";
import { OlapParamsService } from "../OlapScores/OlapParamsService";
import { OnLastLevelClickAction } from "src/components/ScoreDetail/Chart/Distribution/DistributionChartCore";
import { Dimensions } from "../OlapScores/useDimensions";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import type { ScoreDetailRemoteState } from "./scoreDetailRemoteState";

export type ScoreDetailUrls = {
    scoreListUrl: string;
    selectedScoreUrl: string;
};

type ScoreDetailContextProviderProps = {
    scoresParams?: OlapScoresParams;
    score: Score;
    olapScores?: MainScore;
    olapPrevScores?: MainScore;
    scoreDetailDimensions?: Dimensions;
    ignoredDrillDownDimensions?: string[];
    maxRequestedDimensions?: number;
    areAggregationsRequested?: boolean;
    olapLocalizedLabelContext?: OlapLocalizedLabelContext;
    fetchPostForAnswers?: FetchPostForAnswers;
    hideSeeMoreDetails?: (dimension: string) => boolean;
    selectedVariations?: VariationsParams;
    scoreWithVariations?: ScoreWithVariation[];
    ssqVersion?: SsqVersion;
    isLoading?: boolean;
    scoreQueryState?: ScoreDetailRemoteState;
    scoreQueryError?: unknown;
    retryScoreQuery?: () => Promise<unknown>;
    prevCampaignLabel: string | undefined;
    children: ReactNode | ReactNode[];
    goBackLink: string;
    scoreDetailBuilder?: ScoreDetailBuilder;
    tabConfig: TabConfig;
};

export const ScoreDetailContextProvider = (props: ScoreDetailContextProviderProps) => {
    const {
        scoresParams,
        score,
        olapScores,
        olapPrevScores,
        scoreDetailDimensions,
        ignoredDrillDownDimensions = [],
        maxRequestedDimensions,
        areAggregationsRequested = false,
        olapLocalizedLabelContext,
        fetchPostForAnswers,
        hideSeeMoreDetails,
        selectedVariations,
        scoreWithVariations,
        ssqVersion,
        isLoading,
        scoreQueryState,
        scoreQueryError,
        retryScoreQuery,
        children,
        goBackLink,
        scoreDetailBuilder,
        tabConfig,
        prevCampaignLabel = "",
    } = props;

    const onDrilldownReset = () => {
        if (!scoresParams || !scoreDetailDimensions || scoreDetailDimensions.length === 0) return;

        scoresParams?.state.setDimensions(OlapParamsService.getInitialDimensions(scoreDetailDimensions));
    };

    const drilldownState = useDrilldown({
        title: score.title,
        goBackLink: goBackLink,
        onReset: onDrilldownReset,
    });

    const getDimensionValueLocalizedLabel = (dimension: string, key: string | number): string | null => {
        return OlapLocalizedLabelService.localize(dimension, key, {
            ...olapLocalizedLabelContext,
            drilldownState,
        });
    };

    const levelColors = useRef(new Map<string, string>());
    const colorKey = (dimension: string, id: string | number) => `${dimension}:${id}`;

    const getDimensionValuesColors = (dimensionValues: Array<{ dimension: string; id: string | number }>): string[] => {
        const parentColor = drilldownState.last?.color;
        const colors =
            parentColor ?
                OlapHeadingColorService.deriveShades(parentColor, dimensionValues.length)
            :   OlapHeadingColorService.resolveMany(dimensionValues, {
                    ssqVersion: ssqVersion ?? olapLocalizedLabelContext?.ssqVersion,
                });

        levelColors.current.clear();
        dimensionValues.forEach(({ dimension, id }, index) =>
            levelColors.current.set(colorKey(dimension, id), colors[index]),
        );

        return colors;
    };

    const getDimensionValueDrilldownAction = (
        dimension: string,
        dimensionValue: DimensionValue | undefined,
        onLastLevelClick?: OnLastLevelClickAction,
    ) => {
        if (dimensionValue === undefined) return undefined;

        const label = getDimensionValueLocalizedLabel(dimension, dimensionValue.id) ?? dimensionValue.label;
        const dimensionValueId = dimensionValue?.id;

        const remainingDimensions = OlapParamsService.getRemainingDimensions(
            [dimension, dimensionValueId ? [dimensionValueId] : undefined],
            scoreDetailDimensions ?? [],
            maxRequestedDimensions,
        );

        return () => {
            if (drilldownState === undefined) return;

            if (
                dimensionValue.dimensions.length === 0 ||
                ignoredDrillDownDimensions.indexOf(dimensionValue.dimensions[0]?.label) > -1
            ) {
                onLastLevelClick?.(dimension, dimensionValue, drilldownState.state);

                return;
            }

            const dimensions = OlapParamsService.getDimensionsWithPrependedDimension(
                [dimension, [dimensionValueId]],
                remainingDimensions,
            );

            scoresParams?.state.setDimensions(dimensions);

            drilldownState.drilldown({
                id: dimensionValue.id.toString(),
                label,
                type: dimension,
                color: levelColors.current.get(colorKey(dimension, dimensionValueId)),
                extraAction: (state) => {
                    const actionDimensions = OlapParamsService.getDimensionsWithPrependedDimension(
                        [dimension, [state.id]],
                        remainingDimensions,
                    );

                    scoresParams?.state.setDimensions(actionDimensions);
                },
            });
        };
    };

    return (
        <ScoreDetailContext.Provider
            value={{
                scoresParams,
                score,
                olapScores,
                olapPrevScores,
                areAggregationsRequested,
                fetchPostForAnswers,
                hideSeeMoreDetails,
                selectedVariations,
                getDimensionValueLocalizedLabel,
                getDimensionValuesColors,
                getDimensionValueDrilldownAction,
                scoreDetailBuilder,
                drilldownState,
                tabConfig,
                scoreWithVariations,
                ssqVersion,
                isLoading,
                scoreQueryState,
                scoreQueryError,
                retryScoreQuery,
                prevCampaignLabel,
            }}
        >
            {children}
        </ScoreDetailContext.Provider>
    );
};
